import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Activity, Zap, Layers, Maximize2, Crosshair, Target, ChevronRight, Mail, Monitor, BarChart, Cpu, Smartphone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { blogPosts } from '../data/blogData';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/ui/LanguageSelector';
import { useInstallPrompt } from '../hooks/useInstallPrompt';

const MainHub: React.FC = () => {
  const { t, lang } = useLanguage();
  const { isInstallable, isInstalled, isIOSSafari, promptInstall } = useInstallPrompt();

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-[#E0E0E0] selection:bg-orange-500/20 font-sans overflow-x-hidden selection:text-orange-500 w-full max-w-full">
      
      {/* Precision Grid Background (Blueprint Style) */}
      <div className="fixed inset-0 z-0 opacity-[0.03] pointer-events-none" 
           style={{ backgroundImage: 'radial-gradient(#ffffff 0.5px, transparent 0.5px)', backgroundSize: '30px 30px' }} />
      
      {/* Subtle Light Streaks (The "Işık Süzülmesi" Effect) */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
         <motion.div 
           animate={{ 
             x: ['-100%', '200%'],
             opacity: [0, 0.05, 0]
           }}
           transition={{ duration: 12, repeat: Infinity, ease: "linear", delay: 2 }}
           className="absolute top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-orange-600/40 to-transparent blur-[40px] -rotate-[35deg] origin-top"
         />
         <motion.div 
           animate={{ 
             x: ['-150%', '250%'],
             opacity: [0, 0.03, 0]
           }}
           transition={{ duration: 15, repeat: Infinity, ease: "linear", delay: 5 }}
           className="absolute top-0 bottom-0 w-[1px] bg-gradient-to-b from-transparent via-orange-600/30 to-transparent blur-[60px] -rotate-[35deg] origin-top"
         />
      </div>
      
      {/* Technical Corner Markers - Hidden on mobile to avoid covering buttons */}
      <div className="fixed top-8 left-8 w-10 h-10 border-t border-l border-white/10 z-50 pointer-events-none hidden md:block" />
      <div className="fixed top-8 right-8 w-10 h-10 border-t border-r border-white/10 z-50 pointer-events-none hidden md:block" />
      <div className="fixed bottom-8 left-8 w-10 h-10 border-b border-l border-white/10 z-50 pointer-events-none hidden md:block" />
      <div className="fixed bottom-8 right-8 w-10 h-10 border-b border-r border-white/10 z-50 pointer-events-none hidden md:block" />

      {/* Ticker Bar (Professional Live Data Simulation) */}
      <div className="fixed bottom-0 left-0 lg:left-24 right-0 z-50 h-7 sm:h-8 bg-black/80 backdrop-blur-md border-t border-white/5 flex items-center overflow-hidden">
         <motion.div 
           animate={{ x: ['0%', '-50%'] }} 
           transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
           className="flex gap-20 whitespace-nowrap text-[8px] font-mono text-white/30 tracking-[0.3em] uppercase"
         >
            {[...Array(10)].map((_, i) => (
              <div key={i} className="flex gap-8 items-center">
                 <span className="text-orange-500 font-bold">● FEED_ACTV</span>
                 <span>CALC_ID: LP-{(9000 + i).toString(16).toUpperCase()}</span>
                 <span className="text-white/50">SF_VAL: {(1.1 + (i * 0.07) % 0.8).toFixed(2)}</span>
                 <span>COORD: 51.5074 N // 0.1278 W</span>
                 <span className="text-emerald-400">PWA_ONLINE</span>
                 <span className="text-orange-500/80">SYSTEM: OPTIMAL</span>
              </div>
            ))}
         </motion.div>
      </div>

      {/* Vertical Side Navigation (Desktop Only) */}
      <aside className="fixed left-0 top-0 bottom-0 w-24 hidden lg:flex flex-col items-center justify-between py-12 border-r border-white/5 z-[60] bg-black/20 backdrop-blur-3xl">
         <div className="rotate-[-90deg] origin-center whitespace-nowrap">
            <span className="text-[10px] font-black tracking-[0.8em] text-white/20 uppercase">Coordinate System v2.0</span>
         </div>
         <div className="flex flex-col gap-8">
            <div className="w-1 h-1 bg-orange-600 rounded-full shadow-[0_0_10px_rgba(234,88,12,0.8)]" />
            <div className="w-1 h-1 bg-white/10 rounded-full" />
            <div className="w-1 h-1 bg-white/10 rounded-full" />
         </div>
         <div className="text-[10px] font-mono text-white/10">00.00.26</div>
      </aside>

      {/* Top Bar - Fully responsive, no overflow on phones or desktop */}
      <nav className="fixed top-0 left-0 lg:left-24 right-0 z-50 py-3 sm:py-5 lg:py-6 px-3 sm:px-6 lg:px-12 flex justify-between items-center bg-black/70 backdrop-blur-xl border-b border-white/5">
        <div className="flex items-center gap-2.5 sm:gap-4 shrink-0">
          <div className="relative">
             <div className="absolute -inset-1 bg-orange-600/20 blur-sm rounded-lg" />
             <Shield className="text-orange-600 w-6 h-6 sm:w-8 sm:h-8 relative" />
          </div>
          <span className="text-lg sm:text-2xl font-black tracking-tighter text-white">
            LASHING<span className="text-white/30">PLAN</span>
          </span>
        </div>
        
        <div className="flex items-center gap-1.5 sm:gap-4 md:gap-8">
           <div className="hidden md:flex gap-8 text-[10px] font-black uppercase tracking-[0.4em] text-white/40">
             <a href="#engine" className="hover:text-white transition-colors">{t('hub.nav.products')}</a>
             <a href="#lab" className="hover:text-white transition-colors">{t('hub.nav.insights')}</a>
             <a href="#archives" className="hover:text-white transition-colors">{t('hub.nav.resources')}</a>
           </div>
           <div className="h-6 w-[1px] bg-white/10 hidden sm:block" />
           <LanguageSelector />
           <Link to="/login" className="px-2.5 sm:px-6 py-1.5 sm:py-2.5 bg-white text-black text-[9px] sm:text-[11px] font-black tracking-wider uppercase hover:bg-orange-600 hover:text-white transition-all shadow-xl rounded-sm whitespace-nowrap shrink-0">
              {t('hub.hero.portal_access')}
           </Link>
        </div>
      </nav>

      <main className="lg:pl-24 relative z-10 w-full overflow-hidden">
        
        {/* Hero Section (Responsive Banner with No Horizontal Overflow) */}
        <section className="relative min-h-[92vh] sm:min-h-screen flex flex-col justify-between pt-[8vh] sm:pt-[10vh] pb-12 sm:pb-16 overflow-hidden border-b border-white/5">
          
          {/* Background Image - INTERACTIVE & ALIVE */}
          <div className="absolute inset-0 z-0 group/hero">
             <img 
               src="/assets/images/multimodal_hero_professional_v1.jpg"
               className="w-full h-full object-contain opacity-85 saturate-[0.9] brightness-95 transition-all duration-1000 ease-out group-hover/hero:saturate-150 group-hover/hero:brightness-110 group-hover/hero:scale-105"
               alt="Cargo ship, freight train, and semi-truck at sunset"
             />
             {/* Dynamic Overlays with Light Streaks */}
             <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B]/90 via-[#0A0A0B]/50 to-transparent z-10 pointer-events-none" />
             <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent z-10 pointer-events-none" />
             
             {/* Cinematic Light Streak */}
             <div className="absolute top-0 right-0 w-full h-full bg-[linear-gradient(135deg,transparent_40%,rgba(234,88,12,0.05)_50%,transparent_60%)] z-15 pointer-events-none animate-pulse-slow" />
             
             {/* Very Subtle Blueprint Grid Overlay */}
             <div className="absolute inset-0 z-20 opacity-[0.03] pointer-events-none" 
                  style={{ backgroundImage: 'radial-gradient(#ffffff 0.5px, transparent 0.5px)', backgroundSize: '50px 50px' }} />
          </div>

          <div className="max-w-7xl w-full mx-auto relative z-30 px-4 sm:px-6 lg:px-8 flex-1 flex flex-col justify-between">
             {/* Ambient Content Glow */}
             <div className="absolute -top-32 -left-32 w-72 sm:w-96 h-72 sm:h-96 bg-orange-600/10 blur-[120px] rounded-full pointer-events-none z-0" />
             
             <motion.div
               initial={{ opacity: 0, x: -30 }}
               animate={{ opacity: 1, x: 0 }}
               transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
               className="w-full relative z-10"
             >
               {/* Status Badge */}
               <div className="flex items-center gap-2 mb-4 sm:mb-8">
                  <Crosshair className="text-orange-600 w-4 h-4 sm:w-5 sm:h-5 animate-spin-slow shrink-0" />
                  <span className="text-[10px] font-black text-orange-500 uppercase tracking-[0.4em]">{t('hub.hero.status')}</span>
               </div>
               
               {/* Hero Title: No whitespace-nowrap, fully responsive */}
               <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.1rem] font-bold leading-[1.12] tracking-tight text-white mb-4 sm:mb-6 font-sans uppercase break-words drop-shadow-[0_2px_12px_rgba(0,0,0,0.7)]">
                 <span>{t('hub.hero.title_main')}</span>
                 <span className="text-orange-500 italic font-sans lowercase tracking-tight block mt-1 sm:mt-2">
                   {t('hub.hero.title_italic')}
                 </span>
               </h1>

               {/* Description */}
               <p className="text-sm sm:text-lg md:text-xl text-white/60 font-light leading-relaxed max-w-2xl">
                 {t('hub.hero.description')}
               </p>
             </motion.div>

             {/* Action Buttons Group (Separated, Independent Motion & Spacing) */}
             <motion.div
               initial={{ opacity: 0, y: 24 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
               className="w-full relative z-10 mt-auto pt-16 sm:pt-24 pb-2 sm:pb-4"
             >
                <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-8 items-start sm:items-center">
                  <Link to="/login" className="flex items-center gap-4 sm:gap-6 group bg-white/5 sm:bg-transparent p-2 sm:p-0 rounded-2xl w-full sm:w-auto">
                     <div className="w-14 h-14 sm:w-18 sm:h-18 rounded-full border border-white/20 flex items-center justify-center group-hover:bg-orange-600 group-hover:border-orange-600 transition-all duration-500 shadow-xl group-hover:shadow-orange-600/40 shrink-0">
                        <ArrowRight className="w-6 h-6 sm:w-7 sm:h-7 group-hover:translate-x-1 transition-transform" />
                     </div>
                     <div className="flex flex-col gap-0.5">
                        <span className="text-[11px] font-black tracking-[0.3em] uppercase text-white/90 group-hover:text-white transition-colors">{t('hub.hero.launch_product')}</span>
                        <span className="text-[9px] font-mono text-white/30 uppercase tracking-widest">{t('hub.hero.portal_access')}</span>
                     </div>
                  </Link>

                  {/* Direct PWA Install Button (Mobile / PWA Ready) */}
                  {(!isInstalled && (isInstallable || isIOSSafari)) && (
                    <button
                      onClick={() => {
                        if (isInstallable) {
                          promptInstall();
                        } else {
                          // Dispatch custom event to trigger iOS guide modal in PWAInstallBanner
                          window.dispatchEvent(new CustomEvent('pwa-show-ios-guide'));
                        }
                      }}
                      className="group flex items-center gap-3 px-5 py-3.5 bg-orange-600/15 border border-orange-500/40 rounded-full backdrop-blur-xl hover:bg-orange-600/30 transition-all text-xs font-bold text-orange-400 hover:text-white"
                    >
                      <Smartphone className="w-4 h-4 text-orange-500 animate-pulse shrink-0" />
                      <span>{lang === 'tr' ? 'Uygulamayı Telefona Yükle' : 'Install Mobile App'}</span>
                    </button>
                  )}

                  <a href="#archives" className="group flex items-center gap-3.5 px-6 py-3.5 sm:px-8 sm:py-4 bg-white/5 border border-white/10 rounded-full backdrop-blur-3xl hover:bg-white/10 transition-all duration-300">
                     <Layers className="text-orange-600 w-4 h-4 group-hover:rotate-180 transition-transform duration-500 shrink-0" />
                     <span className="text-[10px] sm:text-[11px] font-black tracking-[0.2em] uppercase text-white/50 group-hover:text-white transition-colors">{t('hub.hero.read_blog')}</span>
                  </a>
               </div>
             </motion.div>
          </div>

          {/* Side HUD Elements (Anchored right edge, elevated to prevent bottom clipping) */}
          <div className="absolute right-8 lg:right-16 xl:right-24 bottom-28 xl:bottom-36 hidden xl:block z-30">
             <motion.div 
               initial={{ opacity: 0, scale: 0.8 }}
               animate={{ opacity: 1, scale: 1 }}
               transition={{ delay: 0.5, duration: 1.5 }}
               className="relative"
             >
                <div className="w-48 h-48 border border-white/5 rounded-full flex items-center justify-center backdrop-blur-sm">
                   <div className="text-center">
                      <div className="text-[9px] font-black text-white/20 uppercase mb-2 tracking-widest">{t('hub.hero.stability_index')}</div>
                      <div className="text-4xl font-mono text-orange-600 font-bold">0.98</div>
                      <div className="mt-2 flex gap-1 justify-center">
                        {[...Array(5)].map((_, i) => <div key={i} className="w-1 h-1 bg-orange-600/40 rounded-full" />)}
                      </div>
                   </div>
                </div>
                {/* Decorative Tech Circles */}
                <div className="absolute inset-[-20px] border border-white/5 rounded-full animate-spin-slow opacity-20" />
             </motion.div>
          </div>
        </section>

        {/* Products Section */}
        <section id="engine" className="py-16 sm:py-24 px-4 sm:px-8 md:px-16 lg:px-24 bg-[#0F0F10]">
          <div className="max-w-7xl mx-auto">
             <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 md:gap-12 mb-12 md:mb-24">
                <div className="max-w-2xl">
                   <h2 className="text-[10px] font-black text-orange-600 uppercase tracking-[0.8em] sm:tracking-[1em] mb-4 sm:mb-6">{t('hub.products.context')}</h2>
                   <p className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight">{t('hub.products.title')}</p>
                   <p className="text-white/40 mt-4 sm:mt-6 font-medium max-w-md text-sm sm:text-base">{t('hub.products.subtitle')}</p>
                </div>
                <div className="text-[10px] font-mono text-white/30 uppercase tracking-widest italic">Industrial Standard Tools</div>
             </div>

             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-1px bg-transparent md:bg-white/5 md:border md:border-white/5">
                {[
                  { 
                    title: "Lashing\nCalculator", 
                    icon: Zap, 
                    img: '/assets/images/calc_ui.png', 
                    tag: "01",
                    desc: t('hub.products.calculator.description'),
                    btnText: t('hub.products.calculator.button'),
                    link: "/lashing-calculator",
                    status: null,
                    titleColor: "text-white"
                  },
                  { 
                    title: "Inspection Pro", 
                    icon: Monitor, 
                    img: '/assets/images/inspection_pro_ui.png', 
                    tag: "02",
                    desc: t('hub.products.inspection.description'),
                    status: t('hub.products.inspection.status'),
                    titleColor: "text-orange-500"
                  },
                  { 
                    title: "Risk Advisor", 
                    icon: BarChart, 
                    img: '/assets/images/risk_ai.png', 
                    tag: "03",
                    desc: t('hub.products.risk.description'),
                    status: t('hub.products.risk.status'),
                    titleColor: "text-white"
                  }
                ].map((m, i) => (
                  <motion.div key={i} className="group relative bg-[#0A0A0B] p-6 sm:p-8 md:p-12 overflow-hidden min-h-[460px] md:h-[650px] flex flex-col justify-between border border-white/10 md:border-white/5 hover:border-orange-600/40 transition-all duration-700 rounded-2xl md:rounded-none">
                     <div className="flex justify-between items-start relative z-30">
                        <span className="text-[10px] font-black text-white/30 tracking-widest">{m.tag}</span>
                        {m.status ? (
                          <span className="px-3 py-1 bg-orange-600/10 border border-orange-600/20 text-orange-500 text-[8px] font-black tracking-widest uppercase rounded">{m.status}</span>
                        ) : (
                          <m.icon className="text-white/20 group-hover:text-orange-500 transition-colors" />
                        )}
                     </div>

                     {/* Product Title Overlay */}
                     <div className="absolute inset-0 flex items-start justify-center z-20 pointer-events-none px-4 sm:px-12 pt-16 sm:pt-20">
                        <h3 className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black ${m.titleColor} uppercase tracking-tighter leading-[0.95] text-center drop-shadow-2xl group-hover:scale-105 transition-transform duration-700 whitespace-pre-line`}>
                           {m.title}
                        </h3>
                     </div>
                     
                     <div className="relative z-30 mt-auto pt-24 sm:pt-32">
                        <p className="text-xs sm:text-sm text-white/60 leading-relaxed font-medium mb-6 sm:mb-10 max-w-[280px]">
                           {m.desc}
                        </p>
                        {m.btnText ? (
                          <Link to={m.link || "/login"} className="inline-flex items-center gap-3 px-6 py-2.5 sm:px-8 sm:py-3 bg-white text-black text-[10px] font-black tracking-widest uppercase hover:bg-orange-600 hover:text-white transition-all rounded-sm">
                             {m.btnText} <ChevronRight size={14} />
                          </Link>
                        ) : (
                          <div className="text-[10px] font-black tracking-widest uppercase text-white/20 italic">
                             Module Pending...
                          </div>
                        )}
                     </div>

                     <div className="absolute inset-0 opacity-20 grayscale group-hover:grayscale-0 group-hover:opacity-40 transition-all duration-1000 z-10">
                        <img src={m.img} className="w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-1000" alt="Module" />
                     </div>
                  </motion.div>
                ))}
             </div>
          </div>
        </section>

        {/* Stats & Trust Bar */}
        <section className="py-16 sm:py-24 bg-black border-y border-white/5 relative overflow-hidden">
           <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(234,88,12,0.05)_0%,transparent_70%)]" />
           <div className="max-w-7xl mx-auto px-4 sm:px-8 md:px-12 relative z-10">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 md:gap-12 text-center">
                 {[
                   { label: t('hub.products.stats.calculations'), val: "1M+" },
                   { label: t('hub.products.stats.countries'), val: "12+" },
                   { label: t('hub.products.stats.compliance'), val: "IMO/CTU" },
                   { label: t('hub.products.stats.precision'), val: "99.9%" }
                 ].map((stat, i) => (
                   <div key={i} className="group cursor-default p-3 rounded-xl bg-white/[0.02] md:bg-transparent">
                      <div className="text-[9px] sm:text-[10px] font-black text-orange-500 tracking-[0.2em] sm:tracking-[0.4em] uppercase mb-2 sm:mb-4 group-hover:text-white transition-colors duration-500">
                        {stat.label}
                      </div>
                      <div className="text-2xl sm:text-4xl md:text-5xl font-light text-white/70 tracking-tighter group-hover:text-white group-hover:scale-105 transition-all duration-500">
                        {stat.val}
                      </div>
                   </div>
                 ))}
              </div>
           </div>
        </section>

        {/* Global Archives (Lashing Intelligence) */}
        <section id="archives" className="py-16 sm:py-24 px-4 sm:px-8 md:px-16 lg:px-24">
           <div className="max-w-7xl mx-auto">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 md:gap-12 mb-12 md:mb-24">
                 <div className="max-w-2xl">
                    <h2 className="text-[10px] font-black text-orange-600 uppercase tracking-[0.8em] sm:tracking-[1em] mb-4 sm:mb-6">{t('hub.hero.archives')}</h2>
                    <p className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight">{t('hub.nav.insights')}</p>
                    <p className="text-white/40 mt-4 sm:mt-6 font-medium text-sm sm:text-base">{t('hub.hero.archives_subtitle')}</p>
                 </div>
                 <Link to="/lashing-plan-blog" className="px-6 py-2.5 sm:px-8 sm:py-3 border border-white/10 text-white text-[10px] font-black tracking-widest uppercase hover:bg-white hover:text-black transition-all rounded-full flex items-center gap-4">
                    {t('hub.hero.read_blog')} <ArrowRight size={14} />
                 </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
                 {[
                   {
                     category: "SAFETY",
                     time: "5 MIN",
                     author: "Uğur Evcil",
                     title: t('hub.products.blog_articles.article_1.title'),
                     desc: t('hub.products.blog_articles.article_1.description'),
                     img: blogPosts[0]?.image || '/assets/images/lashing_v1.png'
                   },
                   {
                     category: "LOGISTICS",
                     time: "6 MIN",
                     author: "Uğur Evcil",
                     title: t('hub.products.blog_articles.article_2.title'),
                     desc: t('hub.products.blog_articles.article_2.description'),
                     img: blogPosts[1]?.image || '/assets/images/logistics_v1.png'
                   },
                   {
                     category: "COMPLIANCE",
                     time: "4 MIN",
                     author: "Uğur Evcil",
                     title: t('hub.products.blog_articles.article_3.title'),
                     desc: t('hub.products.blog_articles.article_3.description'),
                     img: blogPosts[2]?.image || '/assets/images/compliance_v1.png'
                   },
                   {
                     category: "SAFETY",
                     time: "6 MIN",
                     author: "Uğur Evcil",
                     title: t('hub.products.blog_articles.article_4.title'),
                     desc: t('hub.products.blog_articles.article_4.description'),
                     img: blogPosts[3]?.image || '/assets/images/safety_v1.png'
                   }
                 ].map((post, i) => {
                    const originalPost = blogPosts[i];
                    return (
                      <Link 
                        to={originalPost ? `/lashing-plan-blog/${originalPost.id}` : '/lashing-plan-blog'} 
                        key={i} 
                        className="block group"
                      >
                        <motion.div 
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.1 }}
                          className="flex flex-col items-center text-center cursor-pointer p-4 rounded-2xl bg-white/[0.02] md:bg-transparent"
                        >
                           <div className="aspect-[4/3] w-full overflow-hidden rounded-2xl mb-6 bg-white/5 border border-white/5 group-hover:border-orange-600/30 transition-all duration-700">
                              <img src={post.img} className="w-full h-full object-cover opacity-50 group-hover:opacity-100 group-hover:scale-110 transition-all duration-1000 grayscale group-hover:grayscale-0" alt="Archive" />
                           </div>
                           <div className="flex items-center justify-center gap-2.5 mb-3 flex-wrap">
                              <span className="text-[9px] font-black text-orange-500 tracking-wider lowercase">{post.category}</span>
                              <span className="w-1 h-1 bg-white/20 rounded-full" />
                              <span className="text-[9px] font-black text-white/40 tracking-wider lowercase">{post.time}</span>
                              <span className="w-1 h-1 bg-white/20 rounded-full" />
                              <span className="text-[9px] font-black text-white/50 tracking-wider uppercase">{post.author}</span>
                           </div>
                           <h3 className="text-base sm:text-lg font-medium text-white mb-3 group-hover:text-orange-500 transition-colors leading-snug uppercase tracking-wider">
                              {post.title}
                           </h3>
                           <p className="text-xs text-white/40 font-medium leading-relaxed line-clamp-2 max-w-[240px] mx-auto">
                              {post.desc}
                           </p>
                        </motion.div>
                      </Link>
                    );
                  })}
              </div>
           </div>
        </section>

        {/* Footer (Studio Signature) */}
        <footer className="pt-20 sm:pt-36 md:pt-48 pb-20 sm:pb-32 px-4 sm:px-8 md:px-16 lg:px-24 border-t border-white/5">
           <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 sm:gap-16 lg:gap-20 mb-16 sm:mb-28 lg:mb-40">
                 <div className="lg:col-span-5">
                    <div className="flex items-center gap-3 mb-6 sm:mb-10">
                       <Shield className="text-orange-600 w-7 h-7 sm:w-8 sm:h-8" />
                       <span className="text-2xl font-black tracking-tighter text-white">LASHING<span className="text-white/20">PLAN</span></span>
                    </div>
                    <p className="text-white/40 text-sm leading-relaxed max-w-sm mb-6 sm:mb-12">
                       {t('hub.footer.tagline')}
                    </p>
                    {/* PWA Badge in Footer */}
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 text-white/60 text-xs">
                       <Smartphone className="w-3.5 h-3.5 text-orange-500" />
                       <span>PWA Progressive Web App</span>
                    </div>
                 </div>
                 
                 <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8 sm:gap-12">
                    {[
                      { title: t('hub.footer.systems'), links: [t('hub.products.calculator.title'), t('hub.products.risk.title'), "Fleet Monitoring"] },
                      { title: t('hub.footer.resources'), links: ["IMO CSS", "CTU Code v1.2", { name: "Release Notes", path: "/release-notes" }] },
                      { title: t('hub.footer.legal'), links: [
                        { name: t('hub.footer.privacy'), path: "/privacy" },
                        { name: t('hub.footer.compliance'), path: "/legal-notice" },
                        { name: t('hub.footer.terms'), path: "/terms" }
                      ] }
                    ].map((group, i) => (
                      <div key={i}>
                         <h4 className="text-[10px] font-black text-white/20 uppercase tracking-[0.3em] mb-4 sm:mb-8">{group.title}</h4>
                         <ul className="space-y-4 sm:space-y-6 text-[11px] font-semibold text-white/50 uppercase tracking-wider">
                            {group.links.map((link: any, j) => (
                              <li key={j} className="hover:text-orange-500 cursor-pointer transition-colors">
                                {typeof link === 'string' ? (
                                  link
                                ) : (
                                  <Link to={link.path}>{link.name}</Link>
                                )}
                              </li>
                            ))}
                         </ul>
                      </div>
                    ))}
                 </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-10 text-[9px] font-black tracking-[0.3em] sm:tracking-[0.6em] text-white/20 uppercase border-t border-white/5 pt-10 sm:pt-20 text-center sm:text-left">
                 <div>{t('hub.footer.status_all')}</div>
                 <div>COORDINATES: 51.5074° N, 0.1278° W</div>
              </div>
           </div>
        </footer>

      </main>
    </div>
  );
};

export default MainHub;
